import { useEffect, useState, useCallback } from "react";
import { supabase } from "../lib/supabase";

export interface Comment {
  id: number;
  article_id: number;
  name: string;
  email: string;
  content: string;
  created_at: string;
}

export function useComments(articleId: number | undefined) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchComments = useCallback(async () => {
    if (!articleId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: supaError } = await supabase
        .from("comments")
        .select("*")
        .eq("article_id", articleId)
        .order("created_at", { ascending: false });

      if (supaError) throw supaError;
      setComments(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cargar comentarios");
    } finally {
      setLoading(false);
    }
  }, [articleId]);

  const addComment = useCallback(
    async (name: string, email: string, content: string) => {
      if (!articleId) return { success: false, error: "No hay artículo" };
      try {
        const { error: supaError } = await supabase.from("comments").insert({
          article_id: articleId,
          name,
          email,
          content,
        });

        if (supaError) throw supaError;
        await fetchComments();
        return { success: true, error: null };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : "Error al enviar comentario",
        };
      }
    },
    [articleId, fetchComments]
  );

  useEffect(() => {
    fetchComments();

    if (!articleId) return;

    const channel = supabase
      .channel(`comments-${articleId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "comments", filter: `article_id=eq.${articleId}` },
        () => {
          fetchComments();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [articleId, fetchComments]);

  return { comments, loading, error, addComment, refetch: fetchComments };
}